import { useEffect, useRef, useState } from "react";
import { Camera, UploadSimple, MapPin } from "@phosphor-icons/react";

// Timemark / Timestamp camera: burns date + time + GPS + street address + author
// + project into the image (like the Timestamp Camera / Timemark app).
function stampImage(source, w, h, opts = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, w, h);
  const now = new Date();
  const c = opts.coords;
  const fecha = now.toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const hora = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  const lines = [
    fecha,
    `Hora: ${hora}`,
    opts.address ? opts.address : (c ? null : "Ubicación no disponible"),
    c ? `GPS: ${c.lat}, ${c.lng}` : null,
    opts.project ? `Obra: ${opts.project}` : null,
    opts.author ? `Por: ${opts.author}` : null,
    "Obra en Control",
  ].filter((x) => x !== null && x !== "");
  let fs = Math.max(13, Math.round(w * 0.022));
  const pad = Math.round(fs * 0.8);
  // Auto-ajuste: reduce el tamaño de fuente para que la línea más larga (fecha/hora) SIEMPRE quepa
  ctx.font = `bold ${fs}px Arial, sans-serif`;
  const maxTextW = w - pad * 2;
  let longest = 0;
  lines.forEach((ln) => { longest = Math.max(longest, ctx.measureText(ln).width); });
  if (longest > maxTextW) fs = Math.max(9, Math.floor(fs * maxTextW / longest));
  const gap = Math.round(fs * 0.35);
  const barH = fs * lines.length + pad * 2 + (lines.length - 1) * gap;
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.fillRect(0, h - barH, w, barH);
  ctx.textBaseline = "top";
  ctx.font = `bold ${fs}px Arial, sans-serif`;
  lines.forEach((ln, i) => {
    ctx.fillStyle = i === 0 ? "#FFB27A" : (i === 1 ? "#FFD9BF" : (ln === "Obra en Control" ? "#9CA3AF" : "#ffffff"));
    ctx.fillText(ln, pad, h - barH + pad + i * (fs + gap));
  });
  return canvas;
}

export function TimemarkCamera({ onCapture, author, project }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(false);
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [thumbs, setThumbs] = useState([]);

  // Obtiene ubicación + dirección y resuelve una promesa (para esperarla ANTES de sellar)
  const getLocNow = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ coords: null, address: "" });
    navigator.geolocation.getCurrentPosition(async (p) => {
      const lat = p.coords.latitude.toFixed(6), lng = p.coords.longitude.toFixed(6);
      let addr = "";
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&zoom=18&lat=${lat}&lon=${lng}`, { headers: { "Accept-Language": "es" } });
        const j = await r.json();
        if (j && j.display_name) addr = j.display_name.split(",").slice(0, 4).join(", ");
      } catch (e) { /* opcional */ }
      resolve({ coords: { lat, lng }, address: addr });
    }, () => resolve({ coords: null, address: "" }), { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 });
  });

  // Solo para el indicador en pantalla
  const previewLoc = async () => { const loc = await getLocNow(); setCoords(loc.coords); setAddress(loc.address); };

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setActive(false); };
  useEffect(() => () => stop(), []);

  const start = async () => {
    setError(false); previewLoc();
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s; setActive(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 50);
    } catch (e) { setError(true); }
  };

  const emit = (canvas) => {
    canvas.toBlob((blob) => {
      const file = new File([blob], `bitacora_${Date.now()}.jpg`, { type: "image/jpeg" });
      setThumbs((t) => [...t, URL.createObjectURL(blob)]);
      onCapture(file);
    }, "image/jpeg", 0.85);
  };

  const snap = async () => {
    const v = videoRef.current; if (!v) return;
    const loc = await getLocNow();
    setCoords(loc.coords); setAddress(loc.address);
    emit(stampImage(v, v.videoWidth || 640, v.videoHeight || 480, { ...loc, author, project }));
  };

  const onFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    e.target.value = "";
    const loc = await getLocNow();
    setCoords(loc.coords); setAddress(loc.address);
    const img = new Image();
    img.onload = () => emit(stampImage(img, img.naturalWidth, img.naturalHeight, { ...loc, author, project }));
    img.src = URL.createObjectURL(f);
  };

  return (
    <div>
      {active ? (
        <div>
          <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-[4/3] object-cover rounded-sm bg-black" />
          <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {address || (coords ? `${coords.lat}, ${coords.lng}` : "Obteniendo ubicación…")}</div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={snap} data-testid="timemark-snap-btn" className="flex-1 h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors inline-flex items-center justify-center gap-2"><Camera size={18} weight="bold" /> Capturar con fecha/hora</button>
            <button type="button" onClick={stop} className="h-11 px-4 border border-input rounded-sm text-sm">Cerrar</button>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-input rounded-sm p-4 text-center">
          <button type="button" onClick={start} data-testid="timemark-start-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 text-white rounded-sm font-medium hover:bg-slate-800 transition-colors"><Camera size={18} weight="bold" /> Abrir cámara con marca de tiempo</button>
          {error && <p className="text-xs text-danger mt-2">No se pudo abrir la cámara. Usa la opción de archivo.</p>}
          <div className="mt-2 text-xs text-muted-foreground">o</div>
          <label className="mt-2 inline-flex items-center gap-2 h-9 px-4 border border-input rounded-sm font-medium cursor-pointer hover:bg-muted transition-colors text-sm">
            <UploadSimple size={16} /> Subir foto (se le agrega fecha/hora)
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="timemark-file-input" />
          </label>
        </div>
      )}
      {thumbs.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="timemark-thumbs">
          {thumbs.map((t, i) => <img key={i} src={t} alt="captura" className="w-16 h-16 rounded-sm object-cover border border-border" />)}
        </div>
      )}
    </div>
  );
}
